// Research-topic configuration for the IHG demo wizard. Mirrors the V2 topic
// config structure but is a local copy (no code sharing), with the new
// "Benchmarking & Market Intelligence" group from the intake change spec
// (change 4) and category-specific topics keyed to the IHG taxonomy.

import { IhgEvaluationGoal } from "./IhgDemoTypes";

export interface IhgTopicOption {
    id: string;
    label: string;
    description: string;
    /** Taxonomy category ids this topic is limited to */
    forCategories?: string[];
}

enum TopicId {
    // Benchmarking & Market Intelligence (new for IHG)
    REGIONAL_PRICING = "regional_pricing_comparison",
    HISTORICAL_PRICING = "historical_price_discount_trends",
    RATE_CARDS = "rate_card_benchmarks",
    SHOULD_COST = "should_cost_inputs",
    DEMAND_SUPPLY = "demand_supply_indicators",
    // Core
    FINANCIAL = "financial_stability",
    LEGAL = "legal_regulatory_exposure",
    RISK = "risk_assessment",
    PROFILE = "company_profile",
    COMPETITIVE = "competitive_landscape",
    REPUTATION = "customer_reputation",
    INNOVATION = "innovation_roadmap",
    PRICING = "pricing_total_cost",
    LOCKIN = "contract_lockin_risk",
    SUPPORT = "relationship_support",
    LEVERAGE = "negotiation_leverage",
    // Category-specific
    SECURITY = "security_data_protection",
    UPTIME = "uptime_reliability",
    PRODUCT = "product_capabilities",
    DELIVERY = "delivery_expertise",
    // Deep dives
    ESG = "esg_sustainability",
    RETENTION = "customer_retention_signals",
    CASESTUDIES = "case_studies_references",
    MA = "ma_ownership_risk",
}

// New topic group — spec Screen B, descriptions verbatim
export const BENCHMARKING_TOPICS: IhgTopicOption[] = [
    {
        id: TopicId.REGIONAL_PRICING,
        label: "Regional Pricing Comparison",
        description:
            "Published regional price structures where they exist, regulatory and payout differences, currency practice, and local supplier alternatives",
    },
    {
        id: TopicId.HISTORICAL_PRICING,
        label: "Historical Price & Discount Trends",
        description:
            "Documented price changes, fee-policy shifts, and pricing-relevant market events over the trailing 12–36 months",
    },
    {
        id: TopicId.RATE_CARDS,
        label: "Rate Card Benchmarks",
        description:
            "Role-level rate ranges from public rate schedules, cost buildup, and the Infinity Loop deal corpus; services categories only",
    },
    {
        id: TopicId.SHOULD_COST,
        label: "Should-Cost Inputs",
        description: "Bottom-up cost drivers to model a fair-price reference for this deal",
    },
    {
        id: TopicId.DEMAND_SUPPLY,
        label: "Demand / Supply Indicators",
        description:
            "Capacity, labor, and input-cost signals shifting supplier pricing posture in this market",
    },
];

export const PRICING_TOPICS: IhgTopicOption[] = [
    {
        id: TopicId.PRICING,
        label: "Pricing & Total Cost",
        description: "Pricing models, hidden costs, TCO benchmarks, and cost optimization levers",
    },
    {
        id: TopicId.LOCKIN,
        label: "Contract & Lock-in Risk",
        description: "Exit clauses, data portability, migration support, and vendor dependency",
    },
    {
        id: TopicId.SUPPORT,
        label: "Relationship & Support",
        description:
            "Account management quality, SLA enforcement, escalation paths, and responsiveness",
    },
    {
        id: TopicId.LEVERAGE,
        label: "Negotiation Leverage",
        description:
            "Discount benchmarks, competitive alternatives, contract term flexibility, and renewal timing",
    },
];

export const FINANCIAL_TOPICS: IhgTopicOption[] = [
    {
        id: TopicId.FINANCIAL,
        label: "Financial Stability",
        description: "Revenue trends, cash flow, debt, profitability, and going-concern signals",
    },
    {
        id: TopicId.LEGAL,
        label: "Legal & Regulatory Exposure",
        description: "Active litigation, SEC actions, regulatory fines, and compliance violations",
    },
    {
        id: TopicId.RISK,
        label: "Risk Assessment",
        description:
            "Concentration risk, supply chain exposure, geopolitical factors, and key-person dependencies",
    },
];

export const MARKET_TOPICS: IhgTopicOption[] = [
    {
        id: TopicId.PROFILE,
        label: "Company Profile",
        description:
            "History, size, leadership stability, ownership structure, and strategic direction",
    },
    {
        id: TopicId.COMPETITIVE,
        label: "Competitive Landscape",
        description: "Market share, key competitors, differentiation, and switching alternatives",
    },
    {
        id: TopicId.REPUTATION,
        label: "Customer Reputation",
        description: "Review sentiment, NPS signals, public complaints, and reference quality",
    },
    {
        id: TopicId.INNOVATION,
        label: "Innovation & Roadmap",
        description: "R&D investment, product trajectory, technology moat, and future-proofing",
    },
];

const PLATFORM_CATEGORIES = [
    "cloud_infrastructure",
    "software_saas",
    "cybersecurity",
    "property_management_systems",
    "guest_wifi_connectivity",
    "data_analytics_platforms",
    "influencer_creator_platforms",
    "loyalty_crm_platforms",
    "digital_marketing_technology",
];

const SERVICES_CATEGORIES = [
    "media_advertising_agencies",
    "brand_creative_services",
    "market_research",
    "management_consulting",
    "legal_services",
    "financial_audit_services",
    "staffing_recruitment",
    "training_development",
];

export const TYPE_TOPICS: IhgTopicOption[] = [
    {
        id: TopicId.SECURITY,
        label: "Security & Data Protection",
        description: "SOC 2, ISO 27001, GDPR, breach history, encryption, and access controls",
        forCategories: PLATFORM_CATEGORIES,
    },
    {
        id: TopicId.UPTIME,
        label: "Uptime & Reliability",
        description:
            "SLA history, incident track record, infrastructure redundancy, and DR posture",
        forCategories: PLATFORM_CATEGORIES,
    },
    {
        id: TopicId.PRODUCT,
        label: "Product Capabilities",
        description: "Core features, integrations, API ecosystem, and platform maturity",
        forCategories: PLATFORM_CATEGORIES,
    },
    {
        id: TopicId.DELIVERY,
        label: "Delivery & Expertise",
        description: "Team qualifications, methodology, case outcomes, and domain expertise depth",
        forCategories: SERVICES_CATEGORIES,
    },
];

export const DEEP_DIVE_TOPICS: IhgTopicOption[] = [
    {
        id: TopicId.ESG,
        label: "ESG & Sustainability",
        description:
            "Carbon footprint, DEI metrics, governance practices, and sustainability commitments",
    },
    {
        id: TopicId.RETENTION,
        label: "Customer Retention Signals",
        description: "Churn indicators, renewal rates, logo losses, and satisfaction trends",
    },
    {
        id: TopicId.CASESTUDIES,
        label: "Case Studies & References",
        description: "Published wins, reference customers, testimonials, and documented outcomes",
    },
    {
        id: TopicId.MA,
        label: "M&A & Ownership Risk",
        description:
            "Acquisition likelihood, PE/VC ownership pressure, divestitures, and leadership churn signals",
    },
];

export const CORE_TOPICS: IhgTopicOption[] = [
    ...BENCHMARKING_TOPICS,
    ...PRICING_TOPICS,
    ...FINANCIAL_TOPICS,
    ...MARKET_TOPICS,
];

export const DEEP_DIVE_IDS = DEEP_DIVE_TOPICS.map((t) => t.id);

export function getTopicsForCategory(categoryId: string | undefined): IhgTopicOption[] {
    if (!categoryId) return CORE_TOPICS;
    return [
        ...CORE_TOPICS,
        ...TYPE_TOPICS.filter((t) => t.forCategories?.includes(categoryId)),
    ];
}

export interface IhgGoalRecommendation {
    on: string[];
    off: string[];
    banner: string;
}

const BENCHMARKING_IDS = BENCHMARKING_TOPICS.map((t) => t.id);

// Goal presets: the new benchmarking topics default ON for New Supplier
// Evaluation and Contract Negotiation Prep (spec Screen B annotation).
export const GOAL_RECOMMENDATIONS: Record<string, IhgGoalRecommendation> = {
    [IhgEvaluationGoal.NEW_SUPPLIER_EVALUATION]: {
        on: [
            ...BENCHMARKING_IDS,
            TopicId.FINANCIAL,
            TopicId.LEGAL,
            TopicId.RISK,
            TopicId.PROFILE,
            TopicId.COMPETITIVE,
            TopicId.REPUTATION,
            TopicId.INNOVATION,
            TopicId.PRICING,
            TopicId.LOCKIN,
            TopicId.SUPPORT,
            TopicId.LEVERAGE,
            TopicId.SECURITY,
            TopicId.UPTIME,
            TopicId.PRODUCT,
        ],
        off: [],
        banner: "broad coverage across financial health, market position, and commercial terms to build a complete picture.",
    },
    [IhgEvaluationGoal.RENEWAL_DECISION]: {
        on: [
            TopicId.HISTORICAL_PRICING,
            TopicId.DEMAND_SUPPLY,
            TopicId.FINANCIAL,
            TopicId.LEGAL,
            TopicId.RISK,
            TopicId.PRICING,
            TopicId.LOCKIN,
            TopicId.SUPPORT,
            TopicId.LEVERAGE,
            TopicId.REPUTATION,
        ],
        off: [
            TopicId.REGIONAL_PRICING,
            TopicId.RATE_CARDS,
            TopicId.SHOULD_COST,
            TopicId.PROFILE,
            TopicId.INNOVATION,
        ],
        banner: "focused on pricing trends, lock-in risk, support quality, and customer reputation to inform your renewal position.",
    },
    [IhgEvaluationGoal.COMPETITIVE_COMPARISON]: {
        on: [
            TopicId.REGIONAL_PRICING,
            TopicId.PROFILE,
            TopicId.COMPETITIVE,
            TopicId.REPUTATION,
            TopicId.PRICING,
            TopicId.INNOVATION,
            TopicId.PRODUCT,
            TopicId.LEVERAGE,
        ],
        off: [
            TopicId.HISTORICAL_PRICING,
            TopicId.RATE_CARDS,
            TopicId.SHOULD_COST,
            TopicId.DEMAND_SUPPLY,
            TopicId.LEGAL,
            TopicId.RISK,
            TopicId.LOCKIN,
            TopicId.SUPPORT,
        ],
        banner: "emphasis on market positioning, product capabilities, pricing, and differentiation vs. alternatives.",
    },
    [IhgEvaluationGoal.CONTRACT_NEGOTIATION_PREP]: {
        on: [
            ...BENCHMARKING_IDS,
            TopicId.PRICING,
            TopicId.LEVERAGE,
            TopicId.LOCKIN,
            TopicId.COMPETITIVE,
            TopicId.FINANCIAL,
            TopicId.SUPPORT,
        ],
        off: [TopicId.PROFILE, TopicId.INNOVATION, TopicId.LEGAL, TopicId.RISK, TopicId.REPUTATION],
        banner: "heavy focus on pricing benchmarks, negotiation leverage, lock-in risk, and competitive alternatives for maximum leverage.",
    },
    [IhgEvaluationGoal.RISK_ASSESSMENT]: {
        on: [
            TopicId.FINANCIAL,
            TopicId.LEGAL,
            TopicId.RISK,
            TopicId.LOCKIN,
            TopicId.REPUTATION,
            TopicId.SECURITY,
            TopicId.UPTIME,
        ],
        off: [
            ...BENCHMARKING_IDS,
            TopicId.PROFILE,
            TopicId.COMPETITIVE,
            TopicId.INNOVATION,
            TopicId.PRICING,
            TopicId.LEVERAGE,
            TopicId.SUPPORT,
        ],
        banner: "deep dive into financial stability, legal exposure, security posture, and ownership risk to surface red flags.",
    },
    [IhgEvaluationGoal.DUE_DILIGENCE]: {
        on: [
            TopicId.FINANCIAL,
            TopicId.LEGAL,
            TopicId.RISK,
            TopicId.PROFILE,
            TopicId.REPUTATION,
            TopicId.LOCKIN,
            TopicId.SECURITY,
            TopicId.UPTIME,
        ],
        off: [
            ...BENCHMARKING_IDS,
            TopicId.COMPETITIVE,
            TopicId.INNOVATION,
            TopicId.PRICING,
            TopicId.LEVERAGE,
            TopicId.SUPPORT,
        ],
        banner: "comprehensive risk and compliance review including financials, legal exposure, security certifications, and ownership risk.",
    },
};

// ---- Category Benchmark path: output modules (spec Screen C) ----

export interface IhgOutputModule {
    id: string;
    label: string;
    description: string;
}

export const OUTPUT_MODULES: IhgOutputModule[] = [
    {
        id: "market_landscape",
        label: "Market landscape & sizing",
        description: "Category size, growth, structure, and consolidation activity",
    },
    {
        id: "supplier_profiles",
        label: "Supplier profiles & capability matrix",
        description: "Side-by-side profiles and capability scoring across suppliers in scope",
    },
    {
        id: "pricing_benchmarks",
        label: "Pricing benchmarks & fee models",
        description: "Fee-model taxonomy, benchmark bands by spend tier, scaling economics",
    },
    {
        id: "regional_comparison",
        label: "Regional comparison",
        description: "Pricing, coverage, and supplier availability across selected regions",
    },
    {
        id: "trend_forward_view",
        label: "Trend & 12-month forward view",
        description: "Historical pricing movement and scenario outlook for planning",
    },
    {
        id: "risk_demand_supply",
        label: "Risk & demand-supply signals",
        description: "Category risk factors, vendor viability, and capacity indicators",
    },
    {
        id: "negotiation_levers",
        label: "Negotiation levers",
        description: "The commercial asks this market concedes, with supporting evidence",
    },
];

export const DEFAULT_MODULE_IDS = OUTPUT_MODULES.map((m) => m.id);
